import { useState } from 'react';
import type { MisDatos } from '@medchoice/shared';
import { ApiError } from '../../api/client';
import { paciente } from '../../api/paciente';
import { publico } from '../../api/publico';
import { usePedido } from '../../api/usePedido';
import { Enlace } from '../../router';
import { useSesion } from '../../session/Sesion';
import {
  AvisoError, Boton, Cargando, Casilla, EstadoVacio, Entrada, FalloDeCarga, Icono, Insignia, Pestanas, Selector,
  Tarjeta, useAvisos,
} from '../../ui';
import { soloDigitos, validarTelefono } from '../../forms/validacion';
import { ICONO_AVISO, accionDeAviso } from './avisos';

type Seccion = 'avisos' | 'datos' | 'reclamos';

export function MiCuenta() {
  const { salir } = useSesion();
  const [seccion, setSeccion] = useState<Seccion>('avisos');
  return (
    <div className="mi-cuenta">
      <header className="mis-turnos__encabezado">
        <h1>Mi cuenta</h1>
        <Boton variante="secundario" icono="log-out" onClick={() => void salir()}>Cerrar sesión</Boton>
      </header>
      <Pestanas<Seccion> etiqueta="Mi cuenta" activa={seccion} alElegir={setSeccion}
        pestanas={[{ id: 'avisos', texto: 'Avisos' }, { id: 'datos', texto: 'Mis datos' }, { id: 'reclamos', texto: 'Mis reclamos' }]} />
      {seccion === 'avisos' && <Avisos />}
      {seccion === 'datos' && <MisDatosEditables />}
      {seccion === 'reclamos' && <MisReclamos />}
    </div>
  );
}

function Avisos() {
  const pedido = usePedido(() => paciente.avisos(), []);
  if (pedido.estado === 'cargando') return <Cargando />;
  if (pedido.estado === 'error') return <FalloDeCarga mensaje={pedido.error ?? ''} alReintentar={pedido.recargar} />;
  if (!pedido.datos?.length) return <EstadoVacio icono="bell" titulo="No tenés avisos" cuerpo="Acá te avisamos de tus próximos turnos y reseñas pendientes." />;
  return (
    <div className="mis-turnos__lista">
      {pedido.datos.map((a) => (
        <Tarjeta key={`${a.tipo}-${a.id_turno}`}>
          <div className="aviso-paciente">
            <Icono nombre={ICONO_AVISO[a.tipo]} tamano={20} />
            <div>
              <strong>{a.titulo}</strong>
              <p>{a.cuerpo}</p>
              <Enlace a={accionDeAviso(a).ruta}>{accionDeAviso(a).texto}</Enlace>
            </div>
          </div>
        </Tarjeta>
      ))}
    </div>
  );
}

function MisDatosEditables() {
  const pedido = usePedido(() => paciente.misDatos(), []);
  const { datos: catalogos } = usePedido(() => publico.catalogos(), []);
  if (pedido.estado === 'cargando') return <Cargando />;
  if (!pedido.datos) return <FalloDeCarga mensaje={pedido.error ?? ''} alReintentar={pedido.recargar} />;
  return <FormularioDeDatos datos={pedido.datos} obrasSociales={catalogos?.obras_sociales ?? []} />;
}

function FormularioDeDatos({ datos, obrasSociales }: { datos: MisDatos; obrasSociales: string[] }) {
  const { mostrarAviso } = useAvisos();
  const [telefono, setTelefono] = useState(datos.telefono);
  const [obraSocial, setObraSocial] = useState(datos.obra_social ?? '');
  const [afiliado, setAfiliado] = useState(datos.nro_afiliado ?? '');
  const [mostrarNombre, setMostrarNombre] = useState(datos.mostrar_nombre);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const errorTelefono = validarTelefono(telefono);

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      await paciente.actualizar({ telefono, obra_social: obraSocial || null, nro_afiliado: afiliado || null, mostrar_nombre: mostrarNombre });
      mostrarAviso({ tono: 'exito', titulo: 'Guardamos tus datos' });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos guardar los cambios. Probá de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Tarjeta>
      <div className="acceso__formulario">
        <AvisoError>{error}</AvisoError>
        <div className="acceso__fila">
          <Entrada etiqueta="Nombre y apellido" value={`${datos.nombre} ${datos.apellido}`} disabled />
          <Entrada etiqueta="DNI" value={datos.dni} disabled />
        </div>
        <Entrada etiqueta="Correo electrónico" value={datos.email} disabled ayuda="El correo y el DNI no se pueden cambiar." />
        <Entrada etiqueta="Teléfono" inputMode="tel" value={telefono} error={errorTelefono ?? undefined}
          onChange={(e) => setTelefono(soloDigitos(e.target.value, 15))} />
        <div className="acceso__fila">
          <Selector etiqueta="Obra social" marcador="No tengo / particular" value={obraSocial} opciones={obrasSociales}
            onChange={(e) => setObraSocial(e.target.value)} />
          <Entrada etiqueta="Número de afiliado" maxLength={40} value={afiliado} onChange={(e) => setAfiliado(e.target.value)} />
        </div>
        <Casilla checked={mostrarNombre} onChange={(e) => setMostrarNombre(e.target.checked)}
          etiqueta="Mostrar mi nombre en las reseñas que publique" />
        <div className="reserva__acciones">
          <Boton disabled={Boolean(errorTelefono)} cargando={guardando} onClick={() => void guardar()}>Guardar cambios</Boton>
        </div>
      </div>
    </Tarjeta>
  );
}

const TEXTO_ESTADO = { abierto: 'Abierto', en_proceso: 'En proceso', cerrado: 'Resuelto' } as const;

function MisReclamos() {
  const pedido = usePedido(() => paciente.reclamos(), []);
  if (pedido.estado === 'cargando') return <Cargando />;
  if (!pedido.datos?.length) return <EstadoVacio icono="flag" titulo="No hiciste reclamos" cuerpo="Si tenés un problema con un turno, lo podés contar desde el detalle del turno." />;
  return (
    <div className="mis-turnos__lista">
      {pedido.datos.map((r) => (
        <Tarjeta key={r.id_reclamo}>
          <div className="reclamo-paciente">
            <div>
              <strong>{r.asunto}</strong>
              {r.codigo_turno && <span className="codigo"> #{r.codigo_turno}</span>}
              <p>{r.descripcion}</p>
              {r.resolucion && <p className="reclamo-paciente__resolucion">Respuesta: {r.resolucion}</p>}
            </div>
            <Insignia tono={r.estado === 'cerrado' ? 'confirmado' : r.estado === 'en_proceso' ? 'info' : 'pendiente'}>
              {TEXTO_ESTADO[r.estado]}
            </Insignia>
          </div>
        </Tarjeta>
      ))}
    </div>
  );
}
